/**
 * Normalize rentalItems from API (array or JSON string).
 */
export function normalizeRentalItems(rentalItems) {
  if (!rentalItems) return [];
  if (Array.isArray(rentalItems)) return rentalItems;
  if (typeof rentalItems === 'string') {
    try {
      const parsed = JSON.parse(rentalItems);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function qtySuffix(quantity) {
  return quantity > 1 ? ` (x${quantity})` : '';
}

/**
 * Human-readable lines for rental items.
 * Uses parentBundleTitle (from API enrichment via parentBundleId → rental_products)
 * as the set heading, with jacket/pants/add-ons nested underneath.
 */
export function formatRentalItemLines(rentalItems) {
  const items = normalizeRentalItems(rentalItems);
  if (items.length === 0) return [];

  const mains = items.filter((item) => item.itemType === 'main' || !item.itemType);
  const components = items.filter((item) => item.itemType === 'bundle_component');
  const addOns = items.filter((item) => item.itemType === 'addon');
  const lines = [];
  const usedAddOnIndexes = new Set();

  mains.forEach((item) => {
    const title = item.title || item.productTitle || `Product #${item.productId}`;
    lines.push(`${title}${qtySuffix(item.quantity || 1)}`);
  });

  if (components.length > 0) {
    const groups = {};
    components.forEach((item) => {
      const parentId = item.parentBundleId != null ? String(item.parentBundleId) : 'bundle';
      if (!groups[parentId]) groups[parentId] = [];
      groups[parentId].push(item);
    });

    Object.values(groups).forEach((group) => {
      const setTitle = group.find((i) => i.parentBundleTitle)?.parentBundleTitle;
      if (setTitle) {
        lines.push(`${setTitle}${qtySuffix(group[0].quantity || 1)}`);
        group.forEach((item) => {
          lines.push(`  ${item.title || 'Component'}${qtySuffix(item.quantity || 1)}`);
        });
        addOns.forEach((item, idx) => {
          if (String(item.parentBundleId) !== String(group[0].parentBundleId)) return;
          usedAddOnIndexes.add(idx);
          lines.push(`  + ${item.title || 'Add-on'}${qtySuffix(item.quantity || 1)}`);
        });
      } else {
        const titles = group.map((item) => item.title || 'Component').join(' + ');
        lines.push(`${titles}${qtySuffix(group[0].quantity || 1)}`);
      }
    });
  }

  addOns.forEach((item, idx) => {
    if (usedAddOnIndexes.has(idx)) return;
    lines.push(`+ ${item.title || 'Add-on'}${qtySuffix(item.quantity || 1)}`);
  });

  return lines.length > 0 ? lines : [`${items.length} item(s)`];
}
